import type { Item } from "./types";
import { getSeatSlots, rotateAround } from "./workstation-layout";

export interface SeatAnchor {
  deskId: string;
  slotIndex: number;
  x: number;
  y: number;
  rot: number;
  w: number;
  h: number;
}

/** Returns world-space seat anchors for every exploded desk in the scene. */
export function getAllSeatAnchors(items: Item[]): SeatAnchor[] {
  const anchors: SeatAnchor[] = [];
  for (const it of items) {
    if (it.shape !== "desk-rect" || !it.origin) continue;
    const slots = getSeatSlots(it.origin.shape, it.w, it.h, it.origin.seats);
    const cx = it.x + it.w / 2;
    const cy = it.y + it.h / 2;
    slots.forEach((s, idx) => {
      const p = rotateAround(it.x + s.cx, it.y + s.cy, cx, cy, it.rotation);
      anchors.push({
        deskId: it.id,
        slotIndex: idx,
        x: p.x,
        y: p.y,
        rot: (it.rotation + s.rot) % 360,
        w: s.w,
        h: s.h,
      });
    });
  }
  return anchors;
}

/** Snap a dragged chair (identified by its current world center) to the
 *  nearest unoccupied seat anchor, if any anchor is within `radius` cm. */
export function snapChairToSeat(
  chairId: string,
  centerX: number,
  centerY: number,
  items: Item[],
  radius = 30,
  originCenter?: { x: number; y: number },
): { x: number; y: number; rot: number; w: number; h: number; deskId: string; slotIndex: number } | null {
  const anchors = getAllSeatAnchors(items);
  if (!anchors.length) return null;

  // Build an "occupied" set: any chair other than the one being dragged
  // whose center is within ~5cm of an anchor is treated as occupying it.
  const otherChairs = items.filter((i) => i.shape === "chair" && i.id !== chairId);
  const occupied = new Set<string>();
  for (const a of anchors) {
    for (const c of otherChairs) {
      const ccx = c.x + c.w / 2;
      const ccy = c.y + c.h / 2;
      if (Math.hypot(ccx - a.x, ccy - a.y) < 6) {
        occupied.add(`${a.deskId}:${a.slotIndex}`);
        break;
      }
    }
  }

  let best: { d: number; a: SeatAnchor } | null = null;
  for (const a of anchors) {
    if (occupied.has(`${a.deskId}:${a.slotIndex}`)) continue;
    if (originCenter && Math.hypot(a.x - originCenter.x, a.y - originCenter.y) < 6) continue;
    const d = Math.hypot(a.x - centerX, a.y - centerY);
    if (d < radius && (!best || d < best.d)) best = { d, a };
  }
  if (!best) return null;
  return {
    x: best.a.x,
    y: best.a.y,
    rot: best.a.rot,
    w: best.a.w,
    h: best.a.h,
    deskId: best.a.deskId,
    slotIndex: best.a.slotIndex,
  };
}